/**
 * Thumbs up / down on Partnr replies, stored in partnr_feedback (RLS: users
 * insert and read their own rows). Insert-only: a changed rating adds a row.
 */
import { supabase } from '@/integrations/supabase/client';
import type { PartnrTrace } from '@/lib/partnrTrace';

export type PartnrRating = 'up' | 'down';

/** Which model and mode answered, from the `partnr_meta` stream event. */
export interface PartnrReplyMeta {
  model: string | null;
  mode: 'fast' | 'deep' | null;
}

export const MAX_FEEDBACK_COMMENT = 500;

export function parseReplyMeta(value: unknown): PartnrReplyMeta | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  return {
    model: typeof v.model === 'string' ? v.model.substring(0, 100) : null,
    mode: v.mode === 'fast' || v.mode === 'deep' ? v.mode : null,
  };
}

/** Distinct tool names from the reply's trace, in the order they ran. */
export function toolsUsed(trace: PartnrTrace | undefined): string[] {
  return [...new Set((trace?.steps ?? []).map((s) => s.tool).filter(Boolean))];
}

export interface FeedbackInput {
  chatId: string;
  messageIndex: number;
  messageTimestamp?: string;
  rating: PartnrRating;
  comment?: string | null;
  meta?: PartnrReplyMeta;
  trace?: PartnrTrace;
}

/** Returns false when the insert failed (e.g. not signed in, table missing). */
export async function insertPartnrFeedback(input: FeedbackInput): Promise<boolean> {
  const comment = input.comment?.trim().substring(0, MAX_FEEDBACK_COMMENT) || null;
  const { error } = await supabase.from('partnr_feedback').insert({
    chat_id: input.chatId,
    message_index: input.messageIndex,
    message_timestamp: input.messageTimestamp ?? null,
    rating: input.rating,
    comment,
    model: input.meta?.model ?? null,
    mode: input.meta?.mode ?? null,
    tools_used: toolsUsed(input.trace),
  });
  if (error) {
    console.error('[Partnr] Feedback insert failed:', error);
    return false;
  }
  return true;
}
